import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { CartProvider } from "@/context/CartContext";
import { WishlistProvider } from "@/context/WishlistContext";
import { AdminAuthProvider } from "@/context/AdminAuthContext";
import { useState, useEffect } from "react";

// Components
import LoadingScreen from "@/components/LoadingScreen";
import CookieConsent from "@/components/CookieConsent";
import ScrollToTop from "@/components/ScrollToTop";
import AdminProtectedRoute from "@/components/AdminProtectedRoute";

// Pages
import HomePage from "@/pages/HomePage";
import CatalogPage from "@/pages/CatalogPage";
import ProductPage from "@/pages/ProductPage";
import CartPage from "@/pages/CartPage";
import CheckoutPage from "@/pages/CheckoutPage";
import CheckoutSuccessPage from "@/pages/CheckoutSuccessPage";
import CheckoutCancelPage from "@/pages/CheckoutCancelPage";
import AboutPage from "@/pages/AboutPage";
import ContactPage from "@/pages/ContactPage";
import AppointmentPage from "@/pages/AppointmentPage";
import WishlistPage from "@/pages/WishlistPage";
import PrivacyPage from "@/pages/PrivacyPage";
import TerminiPage from "@/pages/TerminiPage";
import AdminLoginPage from "@/pages/AdminLoginPage";
import AdminPage from "@/pages/AdminPage";
import ProductEditPage from "@/pages/ProductEditPage";
import PreventivoPage from "@/pages/PreventivoPage";
import ArgentoPage from "@/pages/ArgentoPage";
import AccessoriPage from "@/pages/AccessoriPage";

// Layout
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { WhatsAppButton } from "@/components/WhatsAppButton";

function AppContent() {
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith('/admin');

  // Admin routes - no header/footer
  if (isAdminRoute) {
    return (
      <>
        <ScrollToTop />
        <Routes>
          <Route path="/admin/login" element={<AdminLoginPage />} />
          <Route path="/admin" element={
            <AdminProtectedRoute><AdminPage /></AdminProtectedRoute>
          } />
          <Route path="/admin/prodotto/:id" element={
            <AdminProtectedRoute><ProductEditPage /></AdminProtectedRoute>
          } />
        </Routes>
      </>
    );
  }

  // Public routes - with header/footer
  return (
    <div className="min-h-screen flex flex-col">
      <ScrollToTop />
      <Header />
      <main id="main" className="flex-1">
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/collezione" element={<CatalogPage />} />
          <Route path="/argento" element={<ArgentoPage />} />
          <Route path="/accessori" element={<AccessoriPage />} />
          <Route path="/prodotto/:id" element={<ProductPage />} />
          <Route path="/carrello" element={<CartPage />} />
          <Route path="/checkout" element={<CheckoutPage />} />
          <Route path="/checkout/success" element={<CheckoutSuccessPage />} />
          <Route path="/checkout/cancel" element={<CheckoutCancelPage />} />
          <Route path="/chi-siamo" element={<AboutPage />} />
          <Route path="/contatti" element={<ContactPage />} />
          <Route path="/appuntamento" element={<AppointmentPage />} />
          <Route path="/preferiti" element={<WishlistPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/termini" element={<TerminiPage />} />
          <Route path="/preventivo" element={<PreventivoPage />} />
        </Routes>
      </main>
      <Footer />
      <WhatsAppButton />
    </div>
  );
}

function App() {
  const [loading, setLoading] = useState(true);
  const [showContent, setShowContent] = useState(false);

  useEffect(() => {
    const hasLoaded = sessionStorage.getItem('hasLoaded');
    if (hasLoaded) {
      setLoading(false);
      setShowContent(true);
    }
  }, []);

  const handleLoadingComplete = () => {
    sessionStorage.setItem('hasLoaded', 'true');
    setLoading(false);
    setTimeout(() => setShowContent(true), 100);
  };

  return (
    <CartProvider>
      <WishlistProvider>
        <AdminAuthProvider>
          <BrowserRouter>
            {loading && <LoadingScreen onComplete={handleLoadingComplete} />}

            <div className={`transition-opacity duration-500 ${showContent ? 'opacity-100' : 'opacity-0'}`}>
              <AppContent />
            </div>

            <Toaster position="top-right" />
            <CookieConsent />
          </BrowserRouter>
        </AdminAuthProvider>
      </WishlistProvider>
    </CartProvider>
  );
}

export default App;
